import './PostRecordModal.css';

import { pencil } from 'ionicons/icons';
import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router';

import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonToast,
  useIonViewDidEnter,
} from '@ionic/react';
import DynamicButton from 'components/buttons/DynamicButton';

import { Filesystem } from '@capacitor/filesystem';
import { AxiosError } from 'axios';
import MapComponent from 'components/MapComponent';
import { runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import useLocation from 'pages/Record/useLocation';
import { useStores } from '../../models';
import { RunModel } from '../../models/RunModel/RunModel';
import AnalysisButton, { ModalStates } from '../AnalysisButton';

import { ST, Stance } from '../../types';

// hooks
import { useContestParticipation } from '../../hooks/useContestParticipation';
import { useContestSelection } from '../../hooks/useContestSelection';

// services
import { logger } from 'services/Logger';
import { freakClassifierService } from 'services/freak_classifier';
import { rankerService } from 'services/freak_ranker';
import { useCalculationsAPI } from '../../services/freak_estimator/Estimator';
import { useFSM } from '../../services/DongleController/DongleController';
import { useGeoLocation } from 'services/GeoLocation';

// util
import { convertBackendRankingToCompatible } from '../../util/freak_ranker';
import {
  updateUserScoreAfterPlay,
  showScoreUpdateNotification,
} from '../../util/freak_ranker';
import { getLocationString } from 'util/location';
import {
  createRecord,
  getVideoFileInfo,
  recordPreProcessing,
} from '../../util';
import Trans from 'util/Trans';

// constants
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION } from '../../util/constants';

const LOG_PREFIX = '[PostRecordModal.tsx]';

interface PostRecordModalProps {
  isOpen: boolean;
  recordData: ST.RecordSample[];
  runBaseData: RunModel | null;
  closeModal: () => void;
  onOpenRecordInfoModal: (
    ev: React.MouseEvent<HTMLIonButtonElement, MouseEvent>
  ) => void;
}

const PostRecordModal: React.FC<PostRecordModalProps> = observer((props) => {
  const { isOpen, closeModal, recordData, runBaseData, onOpenRecordInfoModal } =
    props;

  // ✅ Alle State-Variablen und Hooks
  const [modalState, setModalState] = useState<ModalStates>(
    ModalStates.PREPARATION
  );
  const [location, setLocation] = useState<GeolocationPosition | null>(null);
  const [trickTitle, setRunTitle] = useState<string | null>(null);
  const [scoreData, setScoreData] = useState<any>(null);

  // ✅ Hooks
  const [present] = useIonToast();
  const { resetLocation } = useLocation();
  const estimator = useCalculationsAPI();
  const fsm = useFSM();
  const { recordList, currentUser, exploreElementStore, appData } = useStores();
  const history = useHistory();
  const contestStatus = useContestParticipation();
  const contestSelection = useContestSelection();

  // ✅ FIXED: Use the GeoLocation service
  const geoLocationService = useGeoLocation();

  const defaultRunTitle = `Run #${recordList.numRuns + 1}`;

  // ✅ FIXED: Add useEffect to get location when modal opens
  useEffect(() => {
    if (isOpen) {
      const getLocation = async () => {
        try {
          // ✅ Use the GeoLocation service
          const position = await geoLocationService.getCurrentLocation();
          // ✅ FIXED: Check for valid coordinates AND ensure they're defined
          if (
            position &&
            typeof position.latitude === 'number' &&
            typeof position.longitude === 'number' &&
            (position.latitude !== 0 || position.longitude !== 0)
          ) {
            // ✅ Convert to GeolocationPosition format for compatibility
            const geoPosition: GeolocationPosition = {
              coords: {
                latitude: position.latitude, // ✅ Now guaranteed to be number
                longitude: position.longitude, // ✅ Now guaranteed to be number
                accuracy: 100, // Default accuracy
                altitude: null,
                altitudeAccuracy: null,
                heading: null,
                speed: null,
              },
              timestamp: Date.now(),
            };
            setLocation(geoPosition);
            console.log(`📍 [PostRecordModal] Location set:`, {
              lat: position.latitude,
              lng: position.longitude,
              source: 'GeoLocationService',
              isDummy:
                position.latitude === 48.786111 &&
                position.longitude === 9.13319,
            });
          } else {
            console.warn(
              `⚠️ [PostRecordModal] Invalid location received:`,
              position
            );
            setLocation(null);
          }
        } catch (error) {
          console.warn(`⚠️ [PostRecordModal] Location service failed:`, error);
          setLocation(null);
        }
      };

      getLocation();
    } else {
      // ✅ Reset location when modal closes
      setLocation(null);
    }
  }, [isOpen, geoLocationService]);

  const renderRef = useRef(0);
  renderRef.current += 1;

  // ✅ DEBUG: Track prop changes
  const prevPropsRef = useRef<PostRecordModalProps | null>(null);
  useEffect(() => {
    if (prevPropsRef.current) {
      const changes = [];
      if (prevPropsRef.current.isOpen !== isOpen)
        changes.push(`isOpen: ${prevPropsRef.current.isOpen} → ${isOpen}`);
      if (prevPropsRef.current.recordData !== recordData)
        changes.push(`recordData changed`);
      if (prevPropsRef.current.runBaseData !== runBaseData)
        changes.push(`runBaseData changed`);

      if (changes.length > 0) {
        console.log(`🔄 [PostRecordModal] PROPS CHANGED:`, changes);
      }
    }
    prevPropsRef.current = props;
  });

  const gotoPreview = (play: ST.Play) => {
    try {
      if (play && recordList.lastTempRun) {
        history.replace(`/trickpreview/${recordList.lastTempRun.id}`);
      } else {
        throw new Error('Missing play data or temp run');
      }
      setModalState(ModalStates.PREPARATION);
      closeModal();
    } catch (error) {
      present({
        message: 'Failed to load run preview.',
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });

      setModalState(ModalStates.PREPARATION);
      closeModal();
      history.replace('/record');
    }
  };

  const abort = async () => {
    try {
      if (runBaseData?.localVideoUri) {
        try {
          const fileInfo = getVideoFileInfo(
            runBaseData.deviceId,
            runBaseData.recordId
          );
          await Filesystem.deleteFile(fileInfo);
        } catch (deleteError) {
          logger.error(
            `${LOG_PREFIX} abort - Failed to delete video file:`,
            deleteError
          );
        }
      }

      closeModal();

      // ✅ SAFE: Navigation with fallback
      try {
        await history.push('/record');
      } catch (navError) {
        logger.error(`${LOG_PREFIX} abort - Navigation failed:`, navError);
        window.location.href = '/';
      }
    } catch (abortError) {
      logger.error(`${LOG_PREFIX} abort - Unhandled error:`, abortError);
      closeModal();
      window.location.href = '/record';
    }
  };

  const handleContestSubmission = async (totalScore: number) => {
    if (!contestSelection.selectedContestId || !currentUser?.uid) {
      logger.info(`${LOG_PREFIX} No contest selected - normal run save`);
      return;
    }

    try {
      // Find the selected contest
      const selectedContest = exploreElementStore.elements.find(
        (element) =>
          element.id === contestSelection.selectedContestId &&
          element.type?.toLowerCase() === 'contest'
      );

      if (!selectedContest) {
        logger.warn(`${LOG_PREFIX} Selected contest not found`);
        contestSelection.deselectContest();
        return;
      }

      if (!selectedContest.isUserParticipating?.(currentUser.uid)) {
        logger.warn(`${LOG_PREFIX} User not participating in selected contest`);
        contestSelection.deselectContest();
        return;
      }

      // ✅ Get current participant info BEFORE updating
      const currentRunCount =
        selectedContest.getParticipantRunCount?.(currentUser.uid) || 0;
      const maxRuns = selectedContest.maxRuns || 3;

      if (currentRunCount >= maxRuns) {
        logger.warn(`${LOG_PREFIX} Contest run limit reached`);
        contestSelection.deselectContest();
        present({
          message: `Contest "${selectedContest.name}" completed! All ${maxRuns} runs used.`,
          duration: MESSAGE_ALIVE_MS,
          color: 'warning',
          position: MESSAGE_POSITION,
        });
        return;
      }

      // ✅ FIXED: Ensure participant exists and update in MobX action
      let participant = selectedContest.getParticipant?.(currentUser.uid);

      if (!participant) {
        const created = selectedContest.createContestParticipant?.(
          currentUser.uid,
          currentUser.displayName || currentUser.email || 'Unknown',
          currentUser.nationality || '',
          currentUser.photoURL || ''
        );

        if (!created) {
          throw new Error('Failed to create contest participant');
        }

        participant = selectedContest.getParticipant?.(currentUser.uid);
      }

      if (!participant) {
        throw new Error('Participant not found after creation');
      }

      const scoreAdded = selectedContest.updateContestParticipantRun?.(
        currentUser.uid,
        totalScore
      );

      if (!scoreAdded) {
        throw new Error('Failed to add score to contest participant');
      }

      const updatedParticipant = selectedContest.getParticipant?.(
        currentUser.uid
      );
      const updatedRunCount =
        selectedContest.getParticipantRunCount?.(currentUser.uid) || 0;
      const updatedBestScore =
        selectedContest.getParticipantBestScore?.(currentUser.uid) || 0;

      try {
        const serverData = selectedContest.toServer?.(selectedContest.id);
        const serverParticipant = serverData?.participants?.find(
          (p: any) => p.userId === currentUser.uid
        );
        if (!serverData) {
          throw new Error('Failed to generate server data');
        }
        if (!serverParticipant) {
          throw new Error('User participant data missing in server data');
        }
        if (serverParticipant.runCount !== updatedRunCount) {
          throw new Error(
            `Server data runCount mismatch: expected ${updatedRunCount}, got ${serverParticipant.runCount}`
          );
        }
        if (selectedContest.environment?.cloudStorage) {
          await selectedContest.environment.cloudStorage.updateExploreElement(
            serverData
          );
          logger.success(`${LOG_PREFIX} Contest saved to backend successfully`);
        } else {
          throw new Error('CloudStorage not available');
        }
      } catch (saveError) {
        present({
          message: `Failed to save contest data: ${
            saveError instanceof Error ? saveError.message : 'Unknown error'
          }`,
          duration: MESSAGE_ALIVE_MS,
          color: 'danger',
          position: MESSAGE_POSITION,
        });
      }

      if (window.contestSelectionUpdate) {
        window.contestSelectionUpdate(
          selectedContest.id,
          updatedRunCount,
          maxRuns
        );
      }

      const isContestCompleted = updatedRunCount >= maxRuns;

      if (isContestCompleted) {
        contestSelection.deselectContest();

        present({
          message: `🏆 Contest "${
            selectedContest.name
          }" completed! Final run: ${updatedRunCount}/${maxRuns} | Score: ${totalScore.toFixed(
            1
          )}`,
          duration: MESSAGE_ALIVE_MS,
          color: 'success',
          position: MESSAGE_POSITION,
        });
      } else {
        present({
          message: `🏆 Contest run ${updatedRunCount}/${maxRuns} submitted! Score: ${totalScore.toFixed(
            1
          )}${totalScore === 0 ? ' (Zero score counted)' : ''}`,
          duration: MESSAGE_ALIVE_MS,
          color: 'success',
          position: MESSAGE_POSITION,
        });
      }

      logger.success(`${LOG_PREFIX} Contest run submitted successfully`);
    } catch (contestError) {
      logger.error(`${LOG_PREFIX} Contest submission failed`, {
        error: contestError,
      });

      present({
        message: `Contest submission failed: ${
          contestError instanceof Error ? contestError.message : 'Unknown error'
        }. Contest remains selected for retry.`,
        duration: MESSAGE_ALIVE_MS,
        color: 'warning',
        position: MESSAGE_POSITION,
      });
    }
  };

  const beginAnalysis = async () => {
    try {
      setModalState(ModalStates.ANALYSING);

      // BLE Device validation
      const bleDevice = fsm.bleDevice.get();
      if (!bleDevice || !bleDevice.entry) {
        logger.error(
          `${LOG_PREFIX} beginAnalysis - No bluetooth device available`
        );
        throw new Error('No bluetooth device for trick');
      }

      if (!runBaseData) {
        logger.error(`${LOG_PREFIX} beginAnalysis - No runBaseData available`);
        present({
          message: 'No figure data available',
          duration: MESSAGE_ALIVE_MS,
          color: 'danger',
          position: MESSAGE_POSITION,
        });
        setModalState(ModalStates.PREPARATION);
        closeModal();
        return;
      }
      const title = trickTitle ?? defaultRunTitle;

      // ---------------------------------------------------
      // Pre-processing of record data
      // ---------------------------------------------------
      recordPreProcessing(recordData, logger, LOG_PREFIX);
      // ✅ FIXED: Convert GeolocationPosition to Location format
      const locationData = location?.coords
        ? {
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
            accuracy: location.coords.accuracy,
            altitude: location.coords.altitude || undefined,
            altitudeAccuracy: location.coords.altitudeAccuracy || undefined,
            heading: location.coords.heading || undefined,
            speed: location.coords.speed || undefined,
          }
        : null;

      const record = await createRecord(
        runBaseData.recordId,
        recordData,
        bleDevice,
        title,
        locationData || { latitude: 0, longitude: 0 },
        toJS(currentUser!),
        (currentUser!.bodyInfo?.stance as Stance) ?? undefined
      );
      // ---------------------------------------------------
      // Save record data to runBaseData and recordList
      // ---------------------------------------------------
      runBaseData.setRecordData(record);
      recordList.addTemp(runBaseData);

      // ---------------------------------------------------
      // freak_estimator
      // ---------------------------------------------------
      try {
        console.log(
          `🔍 [PostRecordModal] Submitting record data for estimation...`
        );
        console.log(`Record data:`, recordData);
        console.log(`Run base data:`, runBaseData);
        const data = await estimator.submitRecordData(runBaseData);
        let enhancedData: ST.Play;
        try {
          // ---------------------------------------------------
          // freak_classifier
          // ---------------------------------------------------
          enhancedData =
            await freakClassifierService.enhancePlayWithClassification(data);
        } catch (classificationError) {
          present({
            message: `Classification failed.`,
            duration: MESSAGE_ALIVE_MS,
            color: 'warning',
            position: MESSAGE_POSITION,
          });

          enhancedData = data;
        }
        try {
          // ---------------------------------------------------
          // freak_ranker
          // ---------------------------------------------------
          const score = await rankerService.calculateScore(enhancedData);
          const totalScore = score.play_score?.total_score || 0;
          const isZeroScore = totalScore === 0;
          if (currentUser && typeof totalScore === 'number') {
            try {
              const updateResult = await currentUser.updateScore(totalScore);
            } catch (scoreUpdateError) {
              logger.error(
                `${LOG_PREFIX} Score update failed:`,
                scoreUpdateError
              );
            }
          }
          const compatibleRanking = convertBackendRankingToCompatible(
            score,
            enhancedData.figures
          );
          setScoreData(compatibleRanking);
          runBaseData.setScoreData(compatibleRanking);
          if (
            location?.coords.latitude !== undefined &&
            location?.coords.longitude !== undefined
          ) {
            const locationString = await getLocationString(
              location.coords.latitude,
              location.coords.longitude
            );
            runBaseData.setLocationData(
              location.coords.latitude,
              location.coords.longitude,
              locationString,
              currentUser?.nationality ?? undefined
            );
          }
          const playWithRanking: ST.Play = {
            ...enhancedData,
            ranking: compatibleRanking,
            metadata:
              location?.coords.latitude !== undefined &&
              location?.coords.longitude !== undefined
                ? {
                    totalScore: score.play_score.total_score,
                    figuresAchieved: score.play_score.figures_achieved,
                    figureCount: score.play_score.figure_count,
                    nationality: currentUser?.nationality || null,
                    location: {
                      latitude: location.coords.latitude,
                      longitude: location.coords.longitude,
                      address: await getLocationString(
                        location.coords.latitude,
                        location.coords.longitude
                      ),
                    },
                    userId: currentUser?.uid,
                    timestamp: new Date().toISOString(),
                  }
                : undefined,
          };
          runBaseData.setPlayData(playWithRanking);
          if (currentUser?.uid) {
            try {
              const scoreSubmitted = await rankerService.submitUserScore(
                playWithRanking,
                currentUser.uid
              );

              if (scoreSubmitted) {
                const previousScore = currentUser.score || 0;
                const playScore = score.play_score.total_score;

                try {
                  const scoreUpdateSuccess = await updateUserScoreAfterPlay(
                    currentUser.uid,
                    playScore,
                    currentUser
                  );

                  if (scoreUpdateSuccess) {
                    showScoreUpdateNotification(
                      previousScore,
                      currentUser.score,
                      playScore,
                      present
                    );
                  }
                } catch (updateError) {
                  if (playScore > 0) {
                    present({
                      message: `Score calculated: ${playScore.toFixed(
                        1
                      )} points!`,
                      duration: MESSAGE_ALIVE_MS,
                      color: 'success',
                      position: MESSAGE_POSITION,
                    });
                  }
                }
              }
            } catch (scoreError) {
              logger.debug(`${LOG_PREFIX} Score submission info:`, scoreError);
            }
          }
          try {
            await handleContestSubmission(totalScore);
          } catch (contestError) {
            present({
              message: `Contest submission failed: ${
                contestError instanceof Error
                  ? contestError.message
                  : 'Unknown error'
              }`,
              duration: MESSAGE_ALIVE_MS,
              color: 'warning',
              position: MESSAGE_POSITION,
            });
          }
          setModalState(ModalStates.ANALYSISDONE);
          gotoPreview(playWithRanking);
        } catch (backendError) {
          if (
            backendError instanceof Error &&
            (backendError.message
              .toLowerCase()
              .includes('no figures provided') ||
              backendError.message
                .toLowerCase()
                .includes('scoring service error (400)'))
          ) {
            const localZeroScore = {
              play_score: {
                total_score: 0,
                figures_achieved: 0,
                figure_count: 0,
              },
              version: 'local-fallback',
            };

            const compatibleRanking = convertBackendRankingToCompatible(
              localZeroScore,
              []
            );
            setScoreData(compatibleRanking);
            runBaseData.setScoreData(compatibleRanking);
            const playWithZeroScore: ST.Play = {
              ...enhancedData,
              ranking: compatibleRanking,
              metadata:
                location?.coords.latitude !== undefined &&
                location?.coords.longitude !== undefined
                  ? {
                      totalScore: 0,
                      figuresAchieved: 0,
                      figureCount: 0,
                      nationality: currentUser?.nationality || null,
                      location: {
                        latitude: location.coords.latitude,
                        longitude: location.coords.longitude,
                        address: await getLocationString(
                          location.coords.latitude,
                          location.coords.longitude
                        ),
                      },
                      userId: currentUser?.uid,
                      timestamp: new Date().toISOString(),
                    }
                  : undefined,
            };
            runBaseData.setPlayData(playWithZeroScore);
            setModalState(ModalStates.ANALYSISDONE);
            gotoPreview(playWithZeroScore);
            return;
          }
          let errorMessage = 'Scoring calculation failed';
          setTimeout(() => {
            try {
              history.push('/record');
            } catch (navError) {
              logger.error(
                `${LOG_PREFIX} Navigation to record page failed, going to home`,
                {
                  error: navError,
                }
              );
              history.push('/');
            }
          }, 1000);
          setModalState(ModalStates.PREPARATION);
          closeModal();
          present({
            message: errorMessage,
            duration: MESSAGE_ALIVE_MS,
            color: 'danger',
            position: MESSAGE_POSITION,
          });
          setTimeout(() => history.replace('/profile'), 1000);
        }
      } catch (estimatorError) {
        setModalState(ModalStates.PREPARATION);
        closeModal();
        present({
          message: `Figure analysis failed.`,
          duration: MESSAGE_ALIVE_MS,
          color: 'danger',
          position: MESSAGE_POSITION,
        });

        try {
          history.replace('/profile');
        } catch (navError) {
          logger.error(
            `${LOG_PREFIX} Navigation failed after estimator error:`,
            navError
          );
        }
        return;
      }
    } catch (estimatorError) {
      setModalState(ModalStates.PREPARATION);
      closeModal();
      present({
        message: `Estimation failed.`,
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });
      try {
        history.replace('/record');
      } catch (navError) {
        logger.error(
          `${LOG_PREFIX} Final fallback navigation failed:`,
          navError
        );
        // Last resort - reload page
        window.location.href = '/record';
      }
    }
  };

  // ✅ FIXED: Nur EINE DataContent Definition mit Debug
  const DataContent = () => {
    const dataContentRenderRef = useRef(0);
    dataContentRenderRef.current += 1;

    // ✅ FIXED: Extract lat/long from location
    const lat = location?.coords?.latitude || 0;
    const long = location?.coords?.longitude || 0;

    // ✅ Get selected contest info
    const selectedContest = contestSelection.selectedContestId
      ? exploreElementStore.elements.find(
          (element) => element.id === contestSelection.selectedContestId
        )
      : null;

    const contestInfo =
      selectedContest && currentUser?.uid
        ? (() => {
            const runCount =
              selectedContest.getParticipantRunCount?.(currentUser.uid) || 0;
            const maxRuns = selectedContest.maxRuns || 3;
            const bestScore =
              selectedContest.getParticipantBestScore?.(currentUser.uid) || 0;

            return {
              name: selectedContest.name,
              runCount,
              maxRuns,
              bestScore,
              isCompleted: runCount >= maxRuns,
            };
          })()
        : null;

    if (!location) {
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: 24,
          }}
        >
          <div
            style={{
              color: 'var(--ion-color-medium)',
              fontSize: 18,
              textAlign: 'center',
              marginBottom: 16,
            }}
          >
            <Trans i18nKey="LOCATION_NOT_AVAILABLE">
              Standort nicht verfügbar. Die Karte kann nicht angezeigt werden.
            </Trans>
          </div>
        </div>
      );
    }

    if (modalState === ModalStates.ANALYSISDONE) {
      const hasBackendScore = scoreData?.play_score;
      const totalScore = scoreData?.play_score?.total_score || 0;

      return (
        <div style={{ padding: '16px' }}>
          {/* Contest info display */}
          {contestInfo && (
            <div
              style={{
                backgroundColor: 'var(--ion-color-primary-tint)',
                padding: '16px',
                borderRadius: '12px',
                marginBottom: '16px',
                border: '2px solid var(--ion-color-primary)',
              }}
            >
              <h3
                style={{
                  margin: '0 0 8px 0',
                  color: 'var(--ion-color-primary)',
                  fontSize: '18px',
                }}
              >
                🏆 Contest: {contestInfo.name}
              </h3>
              <div style={{ fontSize: '14px', color: 'var(--ion-color-dark)' }}>
                <p style={{ margin: '4px 0' }}>
                  <strong>Run:</strong> {contestInfo.runCount + 1}/
                  {contestInfo.maxRuns}
                </p>
                <p style={{ margin: '4px 0' }}>
                  <strong>Best Score:</strong>{' '}
                  {contestInfo.bestScore.toFixed(1)}
                </p>
                <p style={{ margin: '4px 0' }}>
                  <strong>Current Score:</strong> {totalScore.toFixed(1)}
                </p>
                {contestInfo.isCompleted && (
                  <p
                    style={{
                      margin: '8px 0 0 0',
                      color: 'var(--ion-color-warning)',
                      fontWeight: 'bold',
                    }}
                  >
                    Contest will be completed after this run!
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Score display */}
          {hasBackendScore && (
            <div style={{ marginBottom: '16px' }}>
              {/* Your existing score display code */}
            </div>
          )}

          {/* Map */}
          <div
            style={{
              width: '100%',
              height: '200px',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <MapComponent lat={lat} long={long} />
          </div>
        </div>
      );
    } else {
      return (
        <div style={{ width: '100%', height: '100%' }}>
          <MapComponent lat={lat} long={long} />
        </div>
      );
    }
  };

  const [presentEditAlert] = useIonAlert();
  const openEditAlert = () => {
    presentEditAlert({
      header: 'Rename run',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'OK',
          handler: ({ newName }) => {
            setRunTitle(newName);
          },
        },
      ],
      inputs: [
        {
          type: 'text',
          name: 'newName',
          value: trickTitle ?? defaultRunTitle,
        },
      ],
    });
  };

  const handleOpenRecordInfoModal = () => {
    const mockEvent = {
      preventDefault: () => {},
    } as React.MouseEvent<HTMLIonButtonElement, MouseEvent>;
    onOpenRecordInfoModal(mockEvent);
  };

  // Automatische Analyse, wenn aktiviert
  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (
      isOpen &&
      appData?.displaySettings?.activateAutoEstimate &&
      modalState === ModalStates.PREPARATION
    ) {
      timeout = setTimeout(() => {
        beginAnalysis();
      }, 1200);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, appData?.displaySettings?.activateAutoEstimate, modalState]);

  return (
    <IonModal
      isOpen={isOpen}
      className="edit-modal"
      onAbort={() => {
        closeModal();
      }}
      onDidDismiss={() => {
        closeModal();
      }}
      backdropDismiss={false}
    >
      <IonHeader>
        <IonToolbar className="edit-modal-toptoolbar ion-no-border">
          <IonButtons slot="start">
            <AnalysisButton state={modalState} beginAnalysis={beginAnalysis} />
          </IonButtons>
          <IonTitle
            style={{
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '24px',
              }}
            >
              {trickTitle ?? defaultRunTitle}
              <IonIcon
                style={{ marginLeft: '8px' }}
                icon={pencil}
                onClick={openEditAlert}
              />
            </span>
          </IonTitle>
          <IonButtons slot="end">
            <DynamicButton
              iconName="InfoUpIcon"
              text={<Trans i18nKey="Weiter" />}
              buttonType="icon-full"
              fill="clear"
              size="xlarge"
              onClick={handleOpenRecordInfoModal}
            />
            <DynamicButton
              text={<Trans i18nKey="DISCARD">Verwerfen</Trans>}
              iconName="CancelUpIcon"
              fill="clear"
              size="xlarge"
              style={{ borderRadius: '10px' }}
              onClick={abort}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent
        style={{ '--background': 'transparent', '--border-colour': 0 }}
      >
        <DataContent />
      </IonContent>
    </IonModal>
  );
});

export default PostRecordModal;
